import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getDocIntelligenceUpload,
  getDocIntelligenceUploads
} from '../../services/api/implementation/impl';
import {
  DOC_INTEL_POLL_MS,
  getReviewSessionId,
  getUploadId,
  uploadNeedsPolling
} from './docIntelUtils';

export function useDocIntelUploads(dealId) {
  const queryClient = useQueryClient();
  const [selectedUploadId, setSelectedUploadId] = useState(null);

  const listQuery = useQuery({
    queryKey: ['docIntelligenceUploads', dealId],
    queryFn: () => getDocIntelligenceUploads(dealId),
    enabled: !!dealId,
    refetchInterval: (query) => {
      const uploads = query.state.data?.uploads ?? [];
      const needsPoll = uploads.some((u) => uploadNeedsPolling(u.status));
      return needsPoll ? DOC_INTEL_POLL_MS : false;
    }
  });

  const uploads = listQuery.data?.uploads ?? [];

  useEffect(() => {
    if (!uploads.length) {
      setSelectedUploadId(null);
      return;
    }
    const stillExists = uploads.some((u) => getUploadId(u) === selectedUploadId);
    if (!selectedUploadId || !stillExists) {
      setSelectedUploadId(getUploadId(uploads[0]));
    }
  }, [uploads, selectedUploadId]);

  const selectedFromList = useMemo(
    () => uploads.find((u) => getUploadId(u) === selectedUploadId) ?? null,
    [uploads, selectedUploadId]
  );

  const detailQuery = useQuery({
    queryKey: ['docIntelligenceUpload', selectedUploadId],
    queryFn: () => getDocIntelligenceUpload(selectedUploadId),
    enabled: !!selectedUploadId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return uploadNeedsPolling(status) ? DOC_INTEL_POLL_MS : false;
    }
  });

  const selectedUpload = detailQuery.data ?? selectedFromList;
  const reviewSessionId = getReviewSessionId(selectedUpload);

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['docIntelligenceUploads', dealId] });
    if (selectedUploadId) {
      queryClient.invalidateQueries({ queryKey: ['docIntelligenceUpload', selectedUploadId] });
    }
  };

  const selectUpload = (uploadId) => {
    setSelectedUploadId(uploadId);
  };

  return {
    uploads,
    selectedUpload,
    selectedUploadId,
    reviewSessionId,
    selectUpload,
    invalidateAll,
    isLoading: listQuery.isLoading,
    isFetching: listQuery.isFetching || detailQuery.isFetching,
    refetchList: listQuery.refetch
  };
}
